/**
 * Chat window component that renders the timeline for a single chat.
 *
 * Behaviour:
 * - Displays messages and status pills in chronological order.
 * - Shows `UnreadDivider` before the first unread message (only for participants).
 * - Scrolls to the divider (or to the bottom) when the chat is opened.
 * - Triggers a read-receipt when there is at least one unread message.
 * - Divider position is driven entirely by `ChatMessageDTO.read` from the backend.
 * - Operators and other non-participants (`canMarkRead === false`) never see the
 *   "Neue Nachrichten" divider because they have no own read-state concept.
 * - For ONE_TO_ONE chats, shows an "Angesehen" (viewed) system pill — visually
 *   identical to a status notification pill (Chip + timestamp bottom-right) — as
 *   the first timeline entry once `chat.supplierViewedAt` is set by the backend
 *   (buyer/operator view only). This also covers the "viewed but no reply yet"
 *   case: the pill is then the only timeline entry, no separate placeholder needed.
 */

import React, {useCallback, useEffect, useRef} from "react";
import {Box} from "@mui/material";
import type {ChatDTO, ChatMessageDTO, StatusPillDTO} from "./types";
import {ChatType, isMessageEntry, isStatusPillEntry} from "./types";
import {decorateWithUnreadDivider} from "./state/timelineMerger";
import MessageBubble from "./MessageBubble";
import StatusPill from "./StatusPill";
import UnreadDivider from "./UnreadDivider";
import MessageComposer from "./MessageComposer";
import {useSnackbarEx} from "@/lib/snackbarex";
import {NotificationI18N} from "@/translations";
import {useTranslation} from "react-i18next";

type SendMessageFn = (
        requestId: number,
        chatId: number,
        body: string,
        attachments?: File[]
) => Promise<ChatMessageDTO>;

interface ChatWindowProps {
    requestId: number;
    chat: ChatDTO;
    onSendMessage: SendMessageFn;
    onMarkRead: (requestId: number, chatId: number) => Promise<void>;
}

interface ChatComposerProps {
    requestId: number;
    chatId: number;
    onSendMessage: SendMessageFn;
}

/**
 * Composer wrapper that binds `requestId` and the (guaranteed defined) `chatId`
 * to the send handler. Extracted so that `chatId` is typed as `number` here,
 * avoiding the need for an undefined-check inside the callback.
 */
const ChatComposer: React.FC<ChatComposerProps> = ({requestId, chatId, onSendMessage}) => {
    const handleSend = useCallback(
            (body: string, attachments: File[]) => onSendMessage(requestId, chatId, body, attachments),
            [requestId, chatId, onSendMessage]
    );
    return <MessageComposer onSend={handleSend}/>;
};

/**
 * Renders the full chat timeline with composer.
 */
const ChatWindow: React.FC<ChatWindowProps> = ({
    requestId,
    chat,
    onSendMessage,
    onMarkRead,
}) => {
    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();

    const timelineRef = useRef<HTMLDivElement | null>(null);
    const dividerRef = useRef<HTMLDivElement | null>(null);
    const hasMarkedRead = useRef(false);

    const chatId = chat.id;
    const hasUnread = Math.max(0, chat.unreadCount ?? 0) > 0;

    // -------------------------------------------------------------------------
    // Mark-Read-Retrigger: reset `hasMarkedRead` when `hasUnread` transitions
    // from false to true (= new unread messages arrived since last mark-read).
    //
    // Hard-initialised to `false` (not to the current `hasUnread` snapshot) so
    // the edge-detection is independent of the initial render value.  This is
    // safe because `hasMarkedRead` also starts as `false`, meaning mark-read
    // fires on mount whenever `hasUnread` is already true — a false→true edge
    // on `prevHadUnreadRef` is not required for the first run.
    // -------------------------------------------------------------------------
    const prevHadUnreadRef = useRef<boolean>(false);

    useEffect(() => {
        const hadUnread = prevHadUnreadRef.current;
        const unreadAppeared = hasUnread && !hadUnread;

        if (unreadAppeared) {
            // Allow markRead to fire again so new unread messages get a read receipt.
            hasMarkedRead.current = false;
        }

        prevHadUnreadRef.current = hasUnread;
    }, [hasUnread]);

    // -------------------------------------------------------------------------
    // Mark-Read: Operators and other non-participants are not allowed to change
    // the read state (the backend rejects such calls with 403). The backend
    // already returns `unreadCount === 0` and `canMarkRead === false` for
    // non-participants, so the `hasUnread` check below will normally be false.
    // The `chat.canMarkRead` guard here is defense-in-depth: it prevents an
    // accidental API call even if a future change re-introduces `unreadCount > 0`
    // for non-participants, keeping the intent explicit and the behaviour stable.
    // -------------------------------------------------------------------------

    // Mark chat as read when opened and there are unread messages
    useEffect(() => {
        if (hasUnread && !hasMarkedRead.current && chatId !== undefined && chat.canMarkRead === true) {
            hasMarkedRead.current = true;
            onMarkRead(requestId, chatId).catch((error: unknown) => {
                // Reset so a retry is possible on next open.
                hasMarkedRead.current = false;
                showMessage({
                    severity: "error",
                    summary: t(NotificationI18N.chat.window.markReadFailed),
                    error,
                });
            });
        }
    }, [hasUnread, chatId, requestId, onMarkRead, showMessage, t, chat.canMarkRead]);

    // Scroll to divider or bottom on mount
    useEffect(() => {
        const target = dividerRef.current ?? timelineRef.current;
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [chatId]);

    // A persisted chat always has an id; nothing to render when id is missing.
    if (chatId === undefined) return null;

    // Operators and other non-participants (`canMarkRead === false`) have no
    // own read-state concept, so the "Neue Nachrichten" divider is semantically
    // meaningless for them. The flag gates both mark-read calls (above) and the
    // divider rendering (below) on the same condition.
    const isParticipant = chat.canMarkRead === true;

    // For Operators/Admins (non-participants), override bubble alignment so that
    // messages authored by the requester (Buyer) appear right-aligned and supplier
    // messages appear left-aligned — the same visual convention the Buyer sees.
    //
    // Data source:
    //   ONE_TO_ONE chats: companyA = requester, companyB = supplier (per ChatDTO contract).
    //   SYSTEM chats:     forCompany = the company owning this system chat; for Operators
    //                     the backend only surfaces the requester's SYSTEM chat, so
    //                     forCompany is always the requester in this view.
    //
    // Participants (Buyer/Supplier) keep the default own-company logic — requesterCompanyId
    // stays undefined so MessageBubble falls back to the standard alignment path.
    const requesterCompanyId: number | undefined = isParticipant
            ? undefined
            : (chat.companyA?.id ?? chat.forCompany?.id);

    const timeline = chat.timeline ?? [];
    const decorated = decorateWithUnreadDivider(timeline);

    // "Viewed" system pill: shown for ONE_TO_ONE chats once the supplier has
    // viewed the request's message area. The backend only fills `supplierViewedAt`
    // for the buyer/operator view (null in the supplier view), so triggering
    // purely on the field's presence is correct — no extra role check needed here.
    // Rendered as a synthetic StatusPillDTO (kind "SUPPLIER_VIEWED") — the same
    // component used for real backend status pills — so it is visually identical
    // to a status notification pill. It is built outside `decorateWithUnreadDivider`
    // and rendered as the FIRST timeline entry (chronologically correct: viewing
    // precedes any message), which also covers the empty-timeline case without a
    // separate placeholder: a viewed chat with no messages then shows exactly the
    // pill, while a non-viewed empty chat stays neutrally empty.
    const viewedPill: StatusPillDTO | null =
            chat.type === ChatType.ONE_TO_ONE && chat.supplierViewedAt != null
                    ? {
                        kind: "SUPPLIER_VIEWED",
                        companyName: chat.displayName,
                        timestamp: chat.supplierViewedAt,
                    }
                    : null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Timeline */}
            <Box
                sx={{ flex: 1, overflowY: "auto", p: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}
                ref={timelineRef}
            >
                {viewedPill && <StatusPill pill={viewedPill}/>}
                {decorated.map(({entry, hasUnreadDividerBelow, hasUnreadDividerAbove}, index) => {
                    const key = isMessageEntry(entry)
                        ? `msg-${entry.message?.id ?? index}`
                        : `pill-${index}`;

                    return (
                        <React.Fragment key={key}>
                            {isParticipant && hasUnreadDividerAbove && (
                                    <UnreadDivider ref={dividerRef}/>
                            )}
                            {isMessageEntry(entry) && (
                                <MessageBubble
                                    requestId={requestId}
                                    chatId={chatId}
                                    entry={entry}
                                    requesterCompanyId={requesterCompanyId}
                                />
                            )}
                            {isStatusPillEntry(entry) && entry.pill && (
                                    <StatusPill pill={entry.pill}/>
                            )}
                            {isParticipant && hasUnreadDividerBelow && (
                                <UnreadDivider ref={dividerRef} />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>

            {/* Composer — only rendered when the current user is allowed to post */}
            {chat.canWrite ? (
                    <ChatComposer
                            requestId={requestId}
                            chatId={chatId}
                            onSendMessage={onSendMessage}
                />
            ) : null}
        </Box>
    );
};

export default ChatWindow;
